// Portfolio data for Lukman Hakeem

export const portfolioData = {
  personal: {
    name: "Lukman",
    title: "Product Designer",
    tagline: "Designing digital products that simplify complex experiences",
    subtitle: "Pixels with purpose. Interfaces that don't apologize.",
    bio: "As a seasoned Product Designer at Majid Al Futtaim, I've reshaped user experiences across retail and real estate. I've led design for major platforms, delivering innovative solutions for community apps and loyalty programs serving millions.",
    location: "Dubai, UAE",
    yearsOfExperience: "5+ years",
    email: "contact@lukmanhakeem.com",
    linkedin: "https://www.linkedin.com/in/lukmanhakeem",
    calendly: "https://calendly.com/",
    resumeUrl: "/Lukman_Hakeem_Resume.pdf",
    philosophy: "Design is not just what it looks like and feels like. Design is how it works.",
    image: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/z2g4vpfo_IMG_0024.png"
  },

  companies: [
    { name: "Majid Al Futtaim", role: "Product Designer", current: true },
    { name: "Freelance", role: "Design Consultant", current: true }
  ],

  tools: [
    { name: "Figma", category: "Design" },
    { name: "FigJam", category: "Collaboration" },
    { name: "Miro", category: "Collaboration" },
    { name: "Notion", category: "Documentation" },
    { name: "Maze", category: "Testing" },
    { name: "Framer", category: "Prototyping" }
  ],

  skills: [
    {
      title: "UX Research",
      description: "User interviews, usability testing, persona development, and journey mapping to understand user needs and behaviors."
    },
    {
      title: "Product Thinking",
      description: "Strategic product decisions that balance user needs, business goals, and technical constraints."
    },
    {
      title: "Interaction Design",
      description: "Creating intuitive, delightful user experiences through thoughtful interaction patterns and micro-animations."
    },
    {
      title: "Design Systems",
      description: "Building scalable design systems that ensure consistency and efficiency across products."
    },
    {
      title: "Prototyping",
      description: "High-fidelity interactive prototypes for testing and stakeholder communication."
    },
    {
      title: "Usability Testing",
      description: "Conducting moderated and unmoderated testing to validate design decisions."
    }
  ],

  expertise: [
    {
      number: "01",
      title: "Digital Strategy",
      description: "Creating comprehensive digital strategies tailored to business goals and user needs. From industry analysis to goal setting, I construct a roadmap to guide ventures to success.",
      services: ["Strategic Planning", "Market Analysis", "Campaign Development", "Analytics and Insights"]
    },
    {
      number: "02",
      title: "Brand Identity",
      description: "Helping businesses create powerful, memorable brand identities that connect with their customer base. Setting up brand strategy and building comprehensive guidelines.",
      services: ["Brand Strategy", "Logo Design", "Brand Guidelines", "Brand Collateral"]
    },
    {
      number: "03",
      title: "UX Design",
      description: "Designing seamless, intuitive user experiences that captivate and delight. Prioritizing understanding user needs and behaviors, focusing on usability and accessibility.",
      services: ["User Research", "Wireframing and Prototyping", "Usability Testing", "Design Systems"]
    },
    {
      number: "04",
      title: "Visual Design",
      description: "Visually stunning interfaces that are both beautiful and practical. Specializing in clean, modern, and engaging designs to enhance user interaction.",
      services: ["Design Principles", "Art Direction", "Product Design", "Motion Design"]
    }
  ],

  metrics: [
    { label: "Projects Completed", value: "65" },
    { label: "Client Satisfaction", value: "100%" },
    { label: "Web Design", value: "06" },
    { label: "Mobile Apps", value: "12" },
    { label: "E-Commerce", value: "06" },
    { label: "Years Experience", value: "5+" }
  ],

  caseStudies: [
    {
      id: "mal-travel-modifying-flight",
      title: "Modifying Flight",
      subtitle: "Changing a booked flight, without the dread",
      company: "Mal Travel",
      description: "A transparent, guided flow to change flights, fares, and baggage after booking.",
      thumbnail: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/hvo5p1tl_Home-1.png",
      coverVideo: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/db19y1dr_Animated-iPhone-mockups-ezremove.mp4",
      role: "Product Designer",
      timeline: "2026 — 2 months",
      team: "Travel Product Head, Lead Product Designer (Me), Product Owner, iOS & Backend Engineers",
      services: ["UX Design", "Flow Mapping", "UI Design", "Fare Comparison", "Pricing Transparency", "Prototyping"],
      tools: ["Figma", "FigJam"],
      overview: "Mal Travel lets users book and manage flights inside the Mal super-app. I designed the Modifying Flight experience — letting travelers change dates, times and routes, add extra baggage, or cancel a booking — all with transparent pricing, clear refund estimates, and a confident review-and-pay flow.",
      problem: {
        description: "Changing a flight is one of the most stressful, opaque parts of travel. Users fear hidden fees and confusing airline rules, and often abandon a change rather than risk an unexpected charge.",
        painPoints: [
          "Flight-change options are buried and confusing",
          "Hidden change fees erode trust",
          "Multi-leg itineraries are hard to understand",
          "Fare and baggage differences are unclear before paying"
        ],
        impact: "Travelers avoid self-service changes and flood support, raising cost and frustration."
      },
      research: {
        methodology: "Audited airline and OTA change flows, mapped fare-rule complexity with the travel team, and reviewed the most common change requests and their failure points.",
        insights: [
          "Users want to see the price difference before committing",
          "Clear fare-type comparison reduces decision anxiety",
          "Layover and multi-stop details must be scannable",
          "A confident review-and-pay step builds trust"
        ],
        personas: [
          {
            name: "Flexible Traveler",
            goal: "Change a flight quickly without surprise fees",
            frustration: "Change flows feel risky and opaque"
          }
        ]
      },
      solution: {
        goals: [
          "Make changing a flight as easy as booking one",
          "Show transparent price differences at every step",
          "Present fare types and itineraries clearly",
          "Deliver a confident, friction-free review and payment"
        ],
        approach: "I designed a guided manage-booking flow — from comparing fare types and itinerary details, to modifying date/time/route, adding baggage, choosing in-flight extras like meals and special assistance, and even cancelling with a clear refund estimate. Every screen surfaces the price impact up front, so there are no surprises at checkout.",
        constraints: [
          "Complex, airline-specific fare and change rules",
          "Multi-leg and multi-stop itineraries",
          "Real-time price differences and add-on pricing",
          "Consistency with the Mal super-app design system"
        ]
      },
      designProcess: {
        userFlows: [
          "Travel entry from the Mal home",
          "Flight results with filters and Mal's pick",
          "Fare-type selection and itinerary details",
          "Modify booking — change date, time or destination",
          "Add baggage with transparent extra charges",
          "Review and pay, or cancel with a refund estimate"
        ],
        iterations: "Iterated on price-difference visibility and itinerary scannability with product and the travel team.",
        informationArchitecture: {
          root: "Mal · Travel",
          sections: [
            { title: "Travel Hub", items: ["Your Trip", "Where Next", "Travel Goals", "Preferences"] },
            { title: "Flight Search", items: ["Results", "Fare Type", "Flight Details"] },
            { title: "Booking", items: ["Review & Pay", "Booking Confirmed"] },
            { title: "Manage Booking", items: ["Modify Flight", "Add Baggage", "In-Flight Extras", "Cancel & Refund"] }
          ]
        },
        userFlow: {
          stages: [
            { title: "Discover", steps: ["Mal Home", "Travel Hub", "Flight Results"] },
            { title: "Book", steps: ["Fare & Flight Details", "Review & Pay", "Booking Confirmed"] },
            { title: "Manage Booking", steps: ["Modify Flight & Date", "Add Baggage & Extras", "Meals & Assistance"] },
            { title: "Cancel & Refund", steps: ["Select Reason", "Refund Estimate", "Confirm Cancellation"] }
          ]
        }
      },
      uiDesign: {
        layout: "grid",
        description: "A clear, trustworthy modify flow — transparent pricing, scannable itineraries, and a confident review-and-pay moment.",
        highlights: [
          "Price impact surfaced at every step",
          "Scannable multi-stop itinerary timelines",
          "Clear fare-type and add-on comparison",
          "Confident review-and-pay with add-ons"
        ],
        images: [
          { label: "Mal Home — Travel Entry", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/hvo5p1tl_Home-1.png" },
          { label: "Travel Hub", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/h5dq9mcc_Home.png" },
          { label: "Flight Results", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/2ozcbnsu_Flight%20results-2.png" },
          { label: "Select Fare Type", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/2q0hdvd1_Flight%20results-4.png" },
          { label: "Flight Details", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/f3yx3a7m_Flight%20results-3.png" },
          { label: "Review & Pay", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/soehjnic_Flight%20results-5.png" },
          { label: "Booking Confirmed — Manage Hub", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/c6e93y2s_Flight%20results-1.png" },
          { label: "Modify Booking — Change Time", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/trewnqrh_Flight%20selected.png" },
          { label: "Modify Booking — Select New Date", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/5si2ho50_Modify%20Booking.png" },
          { label: "Date Change — Pending Confirmation", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/dn04u065_Flight%20results.png" },
          { label: "In-Flight Extras — Meals", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/qtqcfj5s_In-flight%20extras.png" },
          { label: "In-Flight Extras — Special Assistance", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/0sj1ypfm_In-flight%20extras-2.png" },
          { label: "Add Baggage — Allowance & Extras", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/oyui7yjn_Add%20Baggage.png" },
          { label: "Add Baggage — Summary", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/2475i8n4_Add%20Baggage-1.png" },
          { label: "Cancel Booking — Select Reason", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/34gcz8s0_Cancel%20Booking-1.png" },
          { label: "Cancel Booking — Refund Estimate", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/ug1dl68f_Cancel%20Booking.png" }
        ]
      },
      outcome: {
        impact: [
          "A clear, modify-friendly booking flow ready for build",
          "Transparent pricing at every decision point",
          "Reusable flight components across Mal Travel",
          "A change experience designed to reduce support contact"
        ],
        feedback: "Lukman made changing a flight feel as simple as booking one — no hidden fees, no confusion."
      },
      learnings: [
        "Transparency around price changes is the core of trust in travel",
        "Scannable itinerary timelines beat dense text for multi-stop trips",
        "Designing the review-and-pay moment carefully reduces drop-off"
      ]
    },
    {
      id: "mal-global-accounts",
      title: "Global Accounts",
      subtitle: "Onboarding the world into a USD account",
      company: "Mal Money",
      description: "Designing a compliant, frictionless onboarding for cross-border USD accounts.",
      thumbnail: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/b0xv3nph_Untitled.png",
      coverVideo: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/2qrkhrhz_Scene%20%284%29-ezremove.mp4",
      role: "Product Designer",
      timeline: "April 2026 — 3 months",
      team: "USD Account Head, Lead Product Designer (Me), Product Owner, Compliance Team, iOS & Backend Engineers",
      services: ["UX Design", "Onboarding Flow", "UI Design", "Edge Case Handling", "Compliance UX", "Prototyping"],
      tools: ["Figma", "FigJam"],
      overview: "Global Accounts lets users open a USD account from their phone in minutes. I designed the end-to-end onboarding — from first launch to a verified, ready-to-fund account — balancing consumer-grade speed with the strict KYC and regulatory requirements of cross-border banking.",
      problem: {
        description: "Opening a foreign-currency account traditionally means branch visits, paperwork, and weeks of waiting. Mal wanted a fully digital onboarding that felt effortless, yet still satisfied identity, address, and regulatory checks for a USD account.",
        painPoints: [
          "Heavy KYC and compliance steps drive drop-off",
          "Long, unclear multi-step forms cause abandonment",
          "Address and document verification is error-prone",
          "Regulatory rejections need to be handled gracefully"
        ],
        impact: "Without a streamlined flow, qualified users abandon onboarding before reaching a funded account."
      },
      research: {
        methodology: "Reviewed KYC/compliance requirements with the regulatory team, audited competitor neobank onboarding flows, and mapped every decision point and failure state in the journey.",
        insights: [
          "Users expect bank-grade security but consumer-grade speed",
          "Every extra step before value increases abandonment",
          "Clear edge-case messaging builds trust during sensitive steps",
          "Biometric login dramatically improves repeat access"
        ],
        personas: [
          {
            name: "Cross-border Earner",
            goal: "Hold and receive USD without a foreign bank visit",
            frustration: "Traditional USD accounts are slow and paperwork-heavy"
          }
        ]
      },
      solution: {
        goals: [
          "Reduce onboarding to a few focused, single-purpose steps",
          "Design every happy path alongside its edge cases",
          "Make compliance steps feel reassuring, not bureaucratic",
          "Enable fast, secure re-entry with MPIN + Face ID"
        ],
        approach: "I broke the journey into short, single-purpose steps — phone, OTP, MPIN, biometrics, address, and terms — each with a clear purpose and sense of progress. Crucially, every step was designed with its edge cases up front (wrong OTP, weak PIN, locked profile, failed uploads) so the experience stays calm even when things go wrong.",
        constraints: [
          "Strict KYC and regulatory requirements for USD accounts",
          "Address and document verification with proof uploads",
          "Profile-lock handling for regulatory rejections",
          "Native iOS biometric integration"
        ]
      },
      designProcess: {
        userFlows: [
          "Entrypoint — Get Started vs. Login",
          "Phone number entry and OTP verification",
          "MPIN creation and biometric setup",
          "Address verification with proof of address",
          "Terms & conditions and finish"
        ],
        iterations: "Iterated each step with its edge cases in parallel, refining error copy and progress cues with product and compliance.",
        informationArchitecture: {
          root: "Mal · Global Accounts",
          sections: [
            { title: "Get Started", items: ["Entrypoint", "Sign Up / Log In", "Phone Number"] },
            { title: "Verification", items: ["OTP Verification", "Edge Cases"] },
            { title: "Security", items: ["Create MPIN", "Enable Face ID"] },
            { title: "Compliance", items: ["Verify Address", "Terms & Conditions"] }
          ]
        },
        userFlow: {
          stages: [
            { title: "Get Started", steps: ["Entrypoint", "Sign Up / Log In", "Enter Phone Number"] },
            { title: "Verify", steps: ["OTP Verification", "Handle Edge Cases"] },
            { title: "Secure", steps: ["Create MPIN", "Enable Face ID"] },
            { title: "Comply & Finish", steps: ["Verify Address", "Accept Terms", "Account Ready"] }
          ]
        }
      },
      uiDesign: {
        description: "A calm, focused onboarding system — one task per screen, reassuring copy, and fully designed edge cases.",
        highlights: [
          "Single-purpose steps with clear progress",
          "Every happy path paired with its edge cases",
          "Reassuring, plain-language compliance copy",
          "Fast re-entry with MPIN and Face ID"
        ],
        images: [
          { label: "Entrypoint & Phone Number", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/b0xv3nph_Untitled.png" },
          { label: "OTP Verification & Edge Cases", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/px61p88c_Untitled-1.png" },
          { label: "Create MPIN & Edge Cases", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/164zvnzm_Untitled-2.png" },
          { label: "Enable Face ID", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/qndeb9rt_Untitled-3.png" },
          { label: "Verify Address", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/9d1379u1_Untitled-7.png" },
          { label: "Terms & Conditions", src: "https://customer-assets.emergentagent.com/job_6eeec0bf-5698-4d9f-8dd3-a9977b166e8a/artifacts/5h7ac7hd_Untitled-5.png" }
        ]
      },
      outcome: {
        impact: [
          "Complete, build-ready onboarding spec delivered to engineering",
          "Edge cases documented for every step, reducing dev ambiguity",
          "A faster, clearer path to a verified USD account",
          "Reusable onboarding components for future Mal flows"
        ],
        feedback: "Lukman turned a complex, compliance-heavy account opening into an onboarding our users actually finish — every edge case handled before a single line of code."
      },
      learnings: [
        "Designing edge cases alongside happy paths prevents costly rework later",
        "In regulated flows, clear copy is as important as clean UI",
        "Breaking a complex KYC journey into single-purpose steps reduces drop-off"
      ]
    },
    {
      id: "belong-instalments",
      title: "Instalments Payment",
      subtitle: "Empowering Property Owners to Stay on Track",
      company: "Majid Al Futtaim — Belong App",
      description: "Seamless Instalment Management Inside Belong",
      thumbnail: "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/tz9hb2s5_c46f8f54-f684-4365-baac-b16730a2a17e.gif",
      role: "Lead Product Designer",
      timeline: "Mar 2023 - 3 months",
      team: "Product Manager, 4 Frontend Developers, 2 Backend Developers, 3 QA Engineers",
      services: ["UX Research", "Flow Mapping", "UI Design", "Edge Case Handling", "Dev Handoff", "QA Support"],
      tools: ["Figma", "FigJam", "Miro"],
      overview: "The Instalments Payment module was designed to give property owners a clear, flexible, and secure way to manage their due payments. From viewing outstanding amounts to making partial payments and tracking late penalties, this solution made the payment experience more transparent and self-served.",
      problem: {
        description: "Property owners across three of Majid Al Futtaim's master communities had no way to manage their property payments digitally. Every instalment required a physical visit to the office — paid by cheque, POS machine, cash, or bank transfer. For owners managing high-value properties across Tilal Al Ghaf, Al Zahia, and Al Mouj Oman, this created real friction.",
        painPoints: [
          "No digital payment options available",
          "Manual tracking of payment schedules",
          "Physical office visits required for every payment",
          "Lack of transparency in outstanding amounts",
          "No visibility into late penalties or upcoming dues"
        ],
        impact: "Thousands of property owners forced to use offline payment methods, creating operational bottlenecks and poor user experience."
      },
      research: {
        methodology: "User interviews with 25+ property owners, stakeholder workshops with finance and operations teams, competitive analysis of payment platforms",
        insights: [
          "Users wanted full transparency of payment schedules",
          "Partial payment capability was highly requested",
          "Security and trust were primary concerns for digital payments",
          "Users needed clear visibility of late penalties",
          "Mobile-first approach was essential for on-the-go access"
        ],
        personas: [
          {
            name: "Busy Property Investor",
            goal: "Manage multiple properties efficiently without office visits",
            frustration: "No digital way to track or pay instalments"
          },
          {
            name: "International Owner",
            goal: "Make payments remotely from anywhere in the world",
            frustration: "Forced to coordinate with local representatives for payments"
          }
        ]
      },
      solution: {
        goals: [
          "Enable secure digital card payments within the app",
          "Provide complete transparency of payment schedules",
          "Allow flexible partial payments",
          "Automate late penalty calculations",
          "Build trust through clear payment confirmations"
        ],
        approach: "Designed a comprehensive payment flow from the ground up, including card-saving infrastructure, payment history, and multi-property support. The solution prioritized clarity at every step while maintaining security standards.",
        constraints: [
          "No existing card payment infrastructure",
          "Complex business rules for different property types",
          "Legal compliance requirements across three countries",
          "Integration with legacy backend systems"
        ]
      },
      designProcess: {
        userFlows: [
          "Payment dashboard showing all properties and outstanding amounts",
          "Individual property payment details with breakdown",
          "Card addition and secure payment flow",
          "Payment confirmation and receipt generation",
          "Payment history and transaction tracking"
        ],
        userFlowImage: "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/repyk0fz_instalments-user-flow-landscape.png",
        wireframes: "Created low-fidelity wireframes focusing on information hierarchy and user flow. Tested multiple iterations with stakeholders to validate the payment journey.",
        wireframeImages: [
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/uylwwz7t_6.png",
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/2c7nihkv_7.png"
        ],
        prototype: {
          description: "Interactive prototype demonstrating the complete payment flow with realistic interactions and transitions. The prototype was used for usability testing and stakeholder reviews.",
          link: "https://www.figma.com/proto/weFVmv2mlpbAYrjGaCdiDU/DBU-App?page-id=3821%3A85520&node-id=69720-30769&viewport=1643%2C3269%2C0.35&t=WBmhKf5G9vNAmLjr-1&scaling=scale-down&content-scaling=fixed&starting-point-node-id=69720%3A30769&show-proto-sidebar=1"
        },
        iterations: "Went through 4 major iterations based on user testing and stakeholder feedback. Key changes included simplifying the card input flow and adding more prominent security indicators.",
        informationArchitecture: {
          root: "Belong · Instalments",
          sections: [
            { title: "Payment Dashboard", items: ["My Properties", "Outstanding Amounts", "Due Dates"] },
            { title: "Property Details", items: ["Payment Breakdown", "Late Penalties", "Schedule"] },
            { title: "Pay Now", items: ["Add / Save Card", "Partial Payment", "Confirm & Receipt"] },
            { title: "History", items: ["Transactions", "Receipts"] }
          ]
        }
      },
      uiDesign: {
        description: "The final UI emphasized clarity and trust. Used the existing Belong design system while introducing new components specific to financial transactions.",
        highlights: [
          "Clear payment breakdown with visual hierarchy",
          "Prominent security badges and SSL indicators",
          "Smooth card input experience with validation",
          "Success states with downloadable receipts",
          "Accessible color contrast for all payment states"
        ],
        images: [
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/ti5tcb3p_Scene%2013.png",
          "https://framerusercontent.com/images/vVK14cE2Yq1QfMevwRO8WqcLQM.png?width=1980&height=1485",
          "https://framerusercontent.com/images/M2oNqW5Qh7eETs1ktCRXV5KfTI.png?width=1980&height=1485"
        ]
      },
      outcome: {
        impact: [
          "Enabled digital payments for 10,000+ property owners",
          "Reduced payment processing time by 75%",
          "Decreased office visits by 60%",
          "95% user satisfaction rate in post-launch surveys",
          "Processed over $2M in digital payments in first quarter"
        ],
        feedback: "Users appreciated the transparency and convenience. The finance team reported significant reduction in manual payment reconciliation."
      },
      learnings: [
        "Building trust in financial features requires extra attention to security indicators and confirmation states",
        "Edge cases in payment flows (failed transactions, refunds) need as much design attention as happy paths",
        "Close collaboration with backend team early prevented major implementation issues",
        "Testing with real payment amounts (even in staging) revealed user hesitations we missed in prototypes"
      ]
    },
    {
      id: "belong-visitors",
      title: "Visitors Gatepass",
      subtitle: "Frictionless Visitor Management",
      company: "Majid Al Futtaim — Belong App",
      description: "Enabling Secure and Smart Community Access",
      thumbnail: "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/ivhy7mwn_e209fd89-9882-434e-8423-e02a761ff972.gif",
      role: "Product Designer",
      timeline: "June 2023 - 2 months",
      team: "Product Manager, Lead Product Designer, 3 Frontend Developers, 2 Backend Developers, 2 QA Engineers",
      services: ["User Research", "UX Design", "UI Design", "Persona Definition", "Access Flow Mapping", "Developer Collaboration"],
      tools: ["Figma", "Miro", "Notion"],
      overview: "The Visitors Gatepass module was designed to modernize how residents manage guest entries into their communities. I created a system that made inviting, tracking, and approving visitors simple and secure. From deliveries and friends to brokers and maintenance teams, the solution balances convenience with safety.",
      problem: {
        description: "Community residents had to manually coordinate visitor access through phone calls to security gates. This created delays, miscommunications, and security concerns. The existing system had no digital tracking, making it difficult to manage multiple visitors or pre-approve recurring guests.",
        painPoints: [
          "Manual phone coordination with security for every visitor",
          "No advance approval system",
          "Lack of visitor history or tracking",
          "Security concerns with verbal approvals",
          "Delivery personnel and service providers faced long wait times"
        ],
        impact: "Frustration for residents and security staff, security gaps, and poor visitor experience."
      },
      research: {
        methodology: "Conducted interviews with residents, security personnel, and various visitor types. Observed gate operations during peak hours. Analyzed visitor logs from three communities.",
        insights: [
          "Different visitor types have different needs (guests vs. deliveries vs. service providers)",
          "Residents wanted the ability to pre-approve frequent visitors",
          "Security staff needed quick verification methods",
          "Digital passes needed to work offline at gates",
          "Clear expiry times were essential for security"
        ],
        personas: [
          {
            name: "Busy Resident",
            goal: "Quickly approve visitors without phone calls",
            frustration: "Constantly interrupted for visitor approvals"
          },
          {
            name: "Security Officer",
            goal: "Efficiently verify visitors while maintaining security",
            frustration: "Slow manual verification process"
          }
        ]
      },
      solution: {
        goals: [
          "Create digital gatepass system accessible from mobile",
          "Support multiple visitor types with appropriate workflows",
          "Enable pre-approval for recurring visitors",
          "Provide real-time notifications and tracking",
          "Ensure security with time-based expiry and QR codes"
        ],
        approach: "Mapped distinct visitor types (guests, delivery, brokers, contractors) and their behaviors. Designed fast pass creation while minimizing manual gate-side approvals. Worked closely with operations and security teams to ensure automated expiry flows reduced misuse.",
        constraints: [
          "Had to work with existing gate hardware",
          "Offline capability required for gate operations",
          "Different security policies across communities",
          "Multiple visitor types with varying requirements"
        ]
      },
      designProcess: {
        userFlows: [
          "Quick visitor invite flow with visitor type selection",
          "Pre-scheduled vs. immediate access paths",
          "QR code generation and sharing",
          "Gate-side verification flow",
          "Visitor history and active passes management"
        ],
        userFlowImage: "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/1l3wm92d_visitors-user-flow.png",
        wireframes: "Created comprehensive wireframes covering all visitor types and edge cases. Validated flows with security team to ensure compliance with protocols.",
        iterations: "Multiple iterations focused on simplifying visitor type selection and reducing steps for common use cases (like delivery approvals).",
        informationArchitecture: {
          root: "Belong · Visitors",
          sections: [
            { title: "Invite Visitor", items: ["Guest", "Delivery", "Broker", "Contractor"] },
            { title: "Scheduling", items: ["Immediate Access", "Pre-scheduled"] },
            { title: "Gatepass", items: ["QR Code", "Share Pass", "Expiry Timer"] },
            { title: "Manage", items: ["Active Passes", "Visitor History"] }
          ]
        }
      },
      uiDesign: {
        description: "Designed an intuitive interface that made visitor management effortless. Used clear visual differentiation for different pass types and status indicators.",
        highlights: [
          "One-tap quick invite for common visitor types",
          "Visual visitor type cards with clear iconography",
          "Large, scannable QR codes for gate access",
          "Real-time status updates and notifications",
          "Clear expiry countdown for active passes"
        ],
        images: [
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/juuj4hf8_Scene%2013.png",
          "https://framerusercontent.com/images/irrVokMkTUDFWv0V1KEQsJujqg.png?width=3584&height=2240",
          "https://framerusercontent.com/images/sXQUQYPfKO6HER8H7x7IkyAXruc.png?width=3584&height=2240"
        ]
      },
      outcome: {
        impact: [
          "Reduced average gate wait time by 70%",
          "Processed 50,000+ visitor passes in first 3 months",
          "89% reduction in phone calls to security",
          "98% user adoption rate among residents",
          "Security incidents related to unauthorized access dropped to near zero"
        ],
        feedback: "Both residents and security staff praised the system. Delivery personnel specifically highlighted the improved experience at gates."
      },
      learnings: [
        "Designing for multiple user types (residents, visitors, security) required careful balance of needs",
        "Offline-first approach was critical for reliability at gates",
        "Visual differentiation between pass types significantly improved security staff efficiency",
        "Real-time notifications created peace of mind for residents"
      ]
    },
    {
      id: "belong-home-improvements",
      title: "Home Improvements NOC",
      subtitle: "Redefining Renovation Approvals",
      company: "Majid Al Futtaim — Belong App",
      description: "A Seamless NOC Experience for Modern Communities",
      thumbnail: "https://framerusercontent.com/images/8ZnH6DsE3xgvuO2Wd5mILfhInmk.png?width=1980&height=1485",
      role: "Lead UX Designer",
      timeline: "2024 - 4 months",
      team: "Product Manager, Legal Advisor, Property Managers, 4 Developers",
      services: ["UX Strategy", "UI Design", "Workflow Mapping", "User Testing", "Feature Prioritization", "Developer Handoff"],
      tools: ["Figma", "Miro", "FigJam", "Notion"],
      overview: "The Home Improvements NOC module was built to streamline the process for property owners seeking approval to renovate their units. I led the UX strategy and end-to-end design implementation, ensuring legal compliance, operational clarity, and user confidence across the application journey.",
      problem: {
        description: "Property owners wanting to renovate had to navigate a complex paper-based NOC (No Objection Certificate) process. This involved multiple office visits, unclear documentation requirements, and weeks of waiting with no visibility into approval status. The process was opaque, frustrating, and often led to non-compliant renovations.",
        painPoints: [
          "Complex paper-based application process",
          "Unclear documentation requirements",
          "No visibility into approval status",
          "Multiple office visits required",
          "Long approval times (3-4 weeks average)",
          "Confusion about community renovation guidelines"
        ],
        impact: "High rate of non-compliant renovations, frustrated property owners, and overwhelmed property management teams."
      },
      research: {
        methodology: "Stakeholder workshops with property managers and legal team, interviews with property owners who had gone through renovations, analysis of NOC application documents and rejection reasons.",
        insights: [
          "Most rejections were due to incomplete documentation, not actual violations",
          "Users wanted clear checklists of requirements before starting",
          "Visibility into approval status was the top requested feature",
          "Different renovation types had vastly different requirements",
          "Integration with contractor databases would streamline process"
        ],
        personas: [
          {
            name: "Property Owner",
            goal: "Get renovation approval quickly with clear guidance",
            frustration: "Unclear requirements lead to multiple resubmissions"
          },
          {
            name: "Property Manager",
            goal: "Review applications efficiently while ensuring compliance",
            frustration: "Incomplete applications waste review time"
          }
        ]
      },
      solution: {
        goals: [
          "Create structured, guided application flow",
          "Provide clear documentation checklists upfront",
          "Enable status tracking throughout approval process",
          "Support multiple user types (owners, property managers, contractors)",
          "Ensure legal compliance with community guidelines"
        ],
        approach: "Designed a step-by-step guided flow that educates users about requirements before they start. Implemented smart forms that adapt based on renovation type. Created approval workflow for property managers with clear review criteria.",
        constraints: [
          "Complex legal requirements varying by community",
          "Multiple approval stages with different stakeholders",
          "Document verification and storage requirements",
          "Integration with existing property management systems"
        ]
      },
      designProcess: {
        userFlows: [
          "Renovation type selection with requirement preview",
          "Step-by-step application with document upload",
          "Real-time validation and checklist completion",
          "Status tracking through approval stages",
          "NOC certificate generation and download"
        ],
        userFlowImage: "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/roi8ay9l_noc-user-flow.png",
        wireframes: "Created detailed wireframes for complex multi-step form. Special focus on progressive disclosure to avoid overwhelming users while maintaining legal completeness.",
        iterations: "Conducted 3 rounds of testing with actual property owners. Key improvements included better document upload UX and clearer renovation guidelines.",
        informationArchitecture: {
          root: "Belong · Home Improvements",
          sections: [
            { title: "Start Application", items: ["Renovation Type", "Requirements Preview"] },
            { title: "Application", items: ["Smart Forms", "Document Upload", "Validation"] },
            { title: "Approval", items: ["Status Tracking", "Approval Stages", "Manager Review"] },
            { title: "Completion", items: ["NOC Certificate", "Download"] }
          ]
        }
      },
      uiDesign: {
        description: "Designed a confidence-inspiring interface that guides users through complex requirements. Used progress indicators, inline help, and clear validation to reduce errors.",
        highlights: [
          "Smart renovation type selector with visual previews",
          "Progressive step indicator showing completion",
          "Inline document requirements with examples",
          "Real-time form validation reducing errors",
          "Status dashboard with timeline view",
          "Downloadable NOC certificate"
        ],
        images: [
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/k3yf21my_726_1x_shots_so.png",
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/j52552m4_620_1x_shots_so.png",
          "https://customer-assets.emergentagent.com/job_case-driven-design/artifacts/392y2cms_737_1x_shots_so.png",
          "https://framerusercontent.com/images/A5FDQETs2AnaOyZuByGGzD8KwCI.png?width=1980&height=1485",
          "https://framerusercontent.com/images/fzFqckC7T6QCt7qzNOlbVC1O2Cc.png?width=1980&height=1485"
        ]
      },
      outcome: {
        impact: [
          "Reduced average approval time from 3-4 weeks to 7-10 days",
          "95% application completion rate (vs. 60% with paper forms)",
          "70% reduction in incomplete applications",
          "Property manager review time cut by 50%",
          "92% user satisfaction score"
        ],
        feedback: "Property owners appreciated the clarity and visibility. Property managers reported significantly fewer incomplete applications and easier review process."
      },
      learnings: [
        "Progressive disclosure was key to managing complex forms without overwhelming users",
        "Clear examples of required documents prevented most submission errors",
        "Status visibility reduced support inquiries dramatically",
        "Close collaboration with legal team ensured we didn't sacrifice compliance for usability"
      ]
    },
    {
      id: "share-travel",
      title: "SHARE Travel",
      subtitle: "Designing Seamless Travel Moments",
      company: "Majid Al Futtaim — SHARE Rewards App",
      description: "Enhancing SHARE's New Travel Module",
      thumbnail: "https://framerusercontent.com/images/mAav2TVui0GAo4Wv9vDPgAsthk.png?lossless=1&width=1980&height=1485",
      role: "UI Designer",
      timeline: "2024 - 2 months (Yet to release)",
      team: "Lead Product Designer, Product Manager, 3 Developers",
      services: ["UI Design", "Visual System Alignment", "Interaction Mockups", "Figma Delivery", "Design QA"],
      tools: ["Figma", "SHARE Design System"],
      overview: "SHARE Travel was a newly introduced feature that allowed users to explore and book travel experiences directly within the SHARE Rewards ecosystem. My role focused specifically on elevating the visual interface—refining layouts, harmonizing the look with the SHARE design language, and ensuring a smooth, mobile-first experience.",
      problem: {
        description: "SHARE Rewards wanted to expand into travel bookings to increase platform engagement and provide more value to loyalty members. The initial travel module designs existed but needed significant UI refinement to match SHARE's premium brand standards and provide an inspiring booking experience.",
        painPoints: [
          "Initial designs felt disconnected from SHARE's brand",
          "Travel booking UI lacked visual appeal",
          "Mobile experience needed optimization",
          "Inconsistent component usage",
          "Lack of inspiring travel discovery experience"
        ],
        impact: "Risk of poor user adoption for new travel feature if UI doesn't meet brand expectations."
      },
      research: {
        methodology: "Competitive analysis of travel booking platforms, review of SHARE design system, consultation with lead product designer on brand guidelines.",
        insights: [
          "Travel booking needs to feel aspirational and inspiring",
          "Users expect smooth, familiar booking patterns",
          "Visual consistency with existing SHARE features was crucial",
          "Mobile-first approach essential for on-the-go booking",
          "Clear pricing and rewards integration needed prominence"
        ],
        personas: [
          {
            name: "SHARE Member",
            goal: "Discover and book travel experiences using loyalty points",
            frustration: "Generic travel interfaces don't inspire exploration"
          }
        ]
      },
      solution: {
        goals: [
          "Enhance visual interface to match SHARE brand standards",
          "Create inspiring travel discovery experience",
          "Ensure seamless mobile responsiveness",
          "Maintain consistency with SHARE design language",
          "Optimize booking flow UI"
        ],
        approach: "Although product flow and strategy were already defined, I focused on polishing the interface. Simplified navigation, created intuitive layouts for destinations and booking cards, and optimized for mobile responsiveness. Close collaboration with lead designer and developers for Figma handoff QA.",
        constraints: [
          "Had to work within existing SHARE design system",
          "Product flow already defined, limited to UI enhancements",
          "Tight timeline for release",
          "Integration with existing rewards system"
        ]
      },
      designProcess: {
        userFlows: [
          "Travel home with featured destinations",
          "Destination browsing and filtering",
          "Experience detail pages",
          "Booking flow with rewards integration",
          "Booking confirmation and management"
        ],
        wireframes: "Product wireframes were provided. My focus was on UI refinement, visual hierarchy, and mobile optimization.",
        iterations: "Two major iterations based on feedback from lead designer and brand team, focusing on visual consistency and mobile touch targets.",
        informationArchitecture: {
          root: "SHARE · Travel",
          sections: [
            { title: "Discover", items: ["Featured Destinations", "Browse", "Filter"] },
            { title: "Experience", items: ["Detail Pages", "Pricing", "Rewards"] },
            { title: "Booking", items: ["Booking Flow", "Rewards Integration"] },
            { title: "Manage", items: ["Confirmation", "Booking Management"] }
          ]
        },
        userFlow: {
          stages: [
            { title: "Discover", steps: ["Travel Home", "Browse Destinations", "Filter"] },
            { title: "Explore", steps: ["Experience Details", "View Pricing & Rewards"] },
            { title: "Book", steps: ["Booking Flow", "Apply Rewards Points"] },
            { title: "Confirm", steps: ["Booking Confirmation", "Manage Booking"] }
          ]
        }
      },
      uiDesign: {
        description: "Created a visually cohesive travel experience that feels native to SHARE while providing an inspiring booking interface. Emphasized beautiful imagery, clear information hierarchy, and smooth interactions.",
        highlights: [
          "Hero destination cards with immersive imagery",
          "Consistent use of SHARE design tokens",
          "Optimized mobile booking flow",
          "Clear rewards points integration",
          "Smooth transitions and micro-interactions",
          "Accessible color contrast throughout"
        ],
        images: [
          "https://framerusercontent.com/images/By1v14JtJy89KtuCdnLIbtZ18c.png?width=1980&height=1485",
          "https://framerusercontent.com/images/ofWIssVZdH6AB2F8CMiqr9wrbM.png?width=1980&height=1485"
        ]
      },
      outcome: {
        impact: [
          "UI approved by brand and product leadership",
          "Achieved design system consistency score of 98%",
          "Mobile UI testing showed 30% improvement in task completion",
          "Feature scheduled for release Q2 2025",
          "Positive feedback from internal stakeholders"
        ],
        feedback: "Lead designer and product team praised the visual refinement and brand consistency. Development team appreciated clear Figma handoff documentation."
      },
      learnings: [
        "Working within established design systems requires creative problem-solving within constraints",
        "Visual refinement has significant impact on user perception and trust",
        "Close collaboration with developers during handoff prevents implementation issues",
        "Mobile-first UI considerations are critical for travel booking experiences"
      ]
    }
  ],

  testimonials: [
    {
      name: "Product Manager",
      role: "Majid Al Futtaim",
      text: "Lukman's ability to balance user needs with business requirements is exceptional. His designs significantly improved our product metrics."
    },
    {
      name: "Engineering Lead",
      role: "Belong Team",
      text: "Working with Lukman is a pleasure. His detailed design specs and collaborative approach make implementation smooth."
    }
  ]
};
